import { ReactNode } from "react";
import { Redirect } from "wouter";
import { useAuth } from "@/components/providers/AuthProvider";
import type { PageKey } from "@hawk/shared";
import { canAccessPage, getDefaultAuthorizedPath } from "@/lib/permissions";
import { IssueScreen } from "@/components/layout/IssueScreen";

interface ProtectedRouteProps {
  children: ReactNode;
  pageKey?: PageKey;
}

export function ProtectedRoute({ children, pageKey }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const fallbackPath = getDefaultAuthorizedPath(user);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-muted-foreground text-sm font-mono">
            Chargement…
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  if (pageKey && !canAccessPage(user, pageKey)) {
    if (pageKey === "badges" && fallbackPath) {
      return <Redirect to={fallbackPath} />;
    }

    return (
      <IssueScreen
        badgeLabel="Autorisation requise"
        title="Cette zone n&apos;est pas ouverte pour votre profil."
        description="La plateforme HAWK bloque l&apos;accès à cette page parce que votre niveau d&apos;autorisation ne couvre pas cette fonctionnalité. Les contrôles sont appliqués au niveau de l&apos;interface et de l&apos;API."
        action={
          fallbackPath
            ? { label: "Aller vers mon espace autorise", href: fallbackPath }
            : undefined
        }
        secondaryAction={{ label: "Changer de session", href: "/login", variant: "outline" }}
        sideTitle="Accès refusé"
        sideEyebrow="Portail d’accès"
        sideStatus="Accès refusé"
        sideDetails="Si cette page doit être accessible pour votre rôle, ajustez la permission concernée dans l&apos;administration des rôles."
      />
    );
  }

  return <>{children}</>;
}
